// index.js
// Add Buffer polyfill for Azure Storage SDK
import { Buffer } from 'buffer';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignInForm } from './components/login';
import { SignInFormPass } from './components/forgotPassword.js';
import { SetNewPassword } from './components/setNewPassword.js';
import Settings from './components/Settings';
import UsersManagement from './components/UsersManagement.tsx';
import CustomersManagement from './components/CustomersManagement.tsx';
import Profile from './components/Profile.tsx';
import Test from './components/test.tsx';
import IngredientsManagement from './components/IngredientsManagement';
import { AuthProvider } from './context/AuthProvider.tsx';
import App from './App';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS
import 'react-toastify/dist/ReactToastify.css'; // Import React Toastify CSS
// Import XML-related components
import XmlCreator from './components/XmlCreator';
import XmlViewer from './components/XmlViewer';
// Import React Query Components
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useAuth from './hooks/useAuth.ts';
import ProtectedRoute from './components/ProtectedRoute.tsx';

// Make Buffer available globally
window.Buffer = Buffer;

// Initialize QueryClient
const queryClient = new QueryClient();

// Create a component that handles authentication logic
const ProtectedRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <App>
      <Routes>
        {/* Default route for authenticated users */}
        <Route path="/" element={<Navigate to="/xml-viewer" replace />} />
        
        {/* XML Creator and Viewer Routes */}
        <Route path="/xml-creator" element={
          <ProtectedRoute allowedRoles={['Editor', 'Manager']}>
            <XmlCreator />
          </ProtectedRoute>
        } />
        <Route path="/xml-viewer" element={<XmlViewer />} />

        {/* Nested Routes under /settings */}
        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={['Manager', 'Editor']}>
            <Settings />
          </ProtectedRoute>
        }>
          {/* Default Route: Redirect /settings to /settings/customers */}
          <Route index element={<Navigate replace to="customers" />} />

          {/* Nested Routes */}
          <Route path="users" element={<UsersManagement />} />
          <Route path="customers" element={<CustomersManagement />} />
          <Route path="ingredients" element={<IngredientsManagement />} />
        </Route>

        <Route path="/profile" element={<Profile />} />
      </Routes>
    </App>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthProvider>
      {/* Wrap your application with QueryClientProvider */}
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/test" element={<Test/>}/>
            <Route path="/sign-in" element={<SignInForm />} />
            <Route path="/forgot-password" element={<SignInFormPass />} />
            <Route path="/reset-password" element={<SetNewPassword />} />

            {/* Protected Routes - All authentication logic is handled here */}
            <Route path="/*" element={<ProtectedRoutes />} />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/sign-in" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  </React.StrictMode>
);

reportWebVitals();
